import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  pollPowerTranzStatus,
  type PowerTranzPaymentRow,
  type PowerTranzHoldState,
} from "@/lib/powertranz";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Loader2, RefreshCw, CheckCircle2, Clock, AlertCircle, XCircle,
  CircleDashed, CreditCard, RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LABEL: Record<PowerTranzHoldState, string> = {
  pending: "Awaiting authorization",
  held: "Authorized — held",
  settled: "Settled",
  failed: "Failed",
  expired: "Expired",
  refunded: "Refunded",
  voided: "Voided",
  retrying: "Retrying",
};

const TONE: Record<PowerTranzHoldState, string> = {
  pending: "bg-muted text-muted-foreground",
  held: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  settled: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  failed: "bg-destructive/15 text-destructive",
  expired: "bg-destructive/15 text-destructive",
  refunded: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  voided: "bg-muted text-muted-foreground",
  retrying: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
};

const ICON: Record<PowerTranzHoldState, typeof Clock> = {
  pending: CircleDashed,
  held: Clock,
  settled: CheckCircle2,
  failed: XCircle,
  expired: AlertCircle,
  refunded: RotateCw,
  voided: XCircle,
  retrying: Loader2,
};

const TERMINAL = new Set<PowerTranzHoldState>([
  "settled", "failed", "expired", "refunded", "voided",
]);

const fmt = (cents: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(cents / 100);

const fmtTime = (iso: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return d.toLocaleDateString();
};

type Props = {
  /** When true, only show non-terminal payments (default false). */
  pendingOnly?: boolean;
  /** Max rows to show. */
  limit?: number;
};

export const PowerTranzStatusTracker = ({ pendingOnly = false, limit = 10 }: Props) => {
  const { user } = useAuth();
  const [rows, setRows] = useState<PowerTranzPaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [pollingId, setPollingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    let q = supabase
      .from("powertranz_payments")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (pendingOnly) q = q.not("hold_state", "in", "(settled,failed,expired,refunded,voided)");
    const { data } = await q;
    setRows((data as PowerTranzPaymentRow[] | null) ?? []);
    setLoading(false);
  }, [user, pendingOnly, limit]);

  useEffect(() => { load(); }, [load]);

  // Realtime — react to webhook / poll updates instantly
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel(`pt-payments-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "powertranz_payments", filter: `user_id=eq.${user.id}` },
        () => load(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user, load]);

  // Auto-poll non-terminal rows every 20s while mounted
  useEffect(() => {
    const hasPending = rows.some((r) => !TERMINAL.has(r.hold_state));
    if (!hasPending) return;
    const id = setInterval(() => {
      pollPowerTranzStatus({ allPending: true }).catch(() => {});
    }, 20_000);
    return () => clearInterval(id);
  }, [rows]);

  const refreshAll = async () => {
    setPolling(true);
    try {
      await pollPowerTranzStatus({ allPending: true });
      await load();
      toast.success("Status refreshed");
    } catch (e) {
      toast.error((e as Error).message || "Could not refresh");
    } finally {
      setPolling(false);
    }
  };

  const refreshOne = async (txId: string) => {
    setPollingId(txId);
    try {
      await pollPowerTranzStatus({ transactionIdentifier: txId });
      await load();
    } catch (e) {
      toast.error((e as Error).message || "Could not refresh");
    } finally {
      setPollingId(null);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading payment status…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
        No PowerTranz payments yet.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-primary" />
          <h3 className="font-display font-bold text-sm">PowerTranz status</h3>
        </div>
        <Button size="sm" variant="ghost" onClick={refreshAll} disabled={polling}>
          {polling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1.5 text-xs">Refresh</span>
        </Button>
      </div>

      <ul className="divide-y divide-border">
        {rows.map((r) => {
          const Icon = ICON[r.hold_state] ?? CircleDashed;
          const isPolling = pollingId === r.transaction_identifier;
          const isTerm = TERMINAL.has(r.hold_state);
          return (
            <li key={r.id} className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-sm">{fmt(r.amount_cents, r.currency)}</span>
                    <Badge className={cn("gap-1 font-medium", TONE[r.hold_state])}>
                      <Icon className={cn("h-3 w-3", r.hold_state === "retrying" && "animate-spin")} />
                      {LABEL[r.hold_state]}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {r.feature_key ?? r.payment_type} · {r.transaction_identifier.slice(0, 8)}…
                  </p>
                </div>
                {!isTerm && (
                  <button
                    onClick={() => refreshOne(r.transaction_identifier)}
                    aria-label="Refresh this payment"
                    disabled={isPolling}
                    className="text-muted-foreground hover:text-foreground p-1 -mr-1 disabled:opacity-50"
                  >
                    {isPolling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-[11px] text-muted-foreground">
                <span>Auth: {fmtTime(r.authorized_at)}</span>
                <span>Settle: {fmtTime(r.settled_at)}</span>
                <span>Polled: {fmtTime(r.last_polled_at)}</span>
              </div>

              {r.last_error && !isTerm && (
                <p className="text-[11px] text-destructive flex items-start gap-1">
                  <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span className="truncate">
                    {r.last_error}
                    {r.retry_count > 0 && ` · retry ${r.retry_count}`}
                  </span>
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
